import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { activeOwner, lockOfflineWork } from "@/offline/store";
import { getWorkspace } from "@/api/workspace";
import { disablePush } from "@/pwa/push";
import { ApiError, auth } from "@/api/client";

import i18n, { followServerLanguage, uiLanguage } from "@/i18n";
import { setDateTimeLocale } from "@/i18n/datetime";
import { setCompanyCurrency } from "@/pm/currency";
import type { CompanyCurrency } from "@/pm/currency";

import { useBranding } from "./branding";

export interface CurrentUser {
    id: number;
    name: string;
    login: string;
    partner_id?: number;
    partner_write_date?: string | false;
    is_admin: boolean;
    lang: string;
    ui_lang: string | null;
    direction: "ltr" | "rtl";
    date_format?: string;
    time_format?: string;
    company: { id: number; name: string; currency?: CompanyCurrency | null } | null;
    companies: { id: number; name: string }[];
}

interface AuthState {
    user: CurrentUser | null;
    loading: boolean;
    connectionFailed: boolean;
    retryConnection(): void;
    /** where the identity provider sends someone with no session */
    loginUrl: string | null;
    signOut(): Promise<void>;
    chooseCompany(companyId: number): Promise<void>;
    chooseLanguage(code: string): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

function followFormats(user: { lang?: string; date_format?: string; time_format?: string }) {
    if (user.lang) {
        setDateTimeLocale(user.lang, user.date_format, user.time_format);
    }
}

/** every amount is written in the currency of the company the user works in */
function followCompany(user: { company?: CurrentUser["company"] }) {
    setCompanyCurrency(user.company?.currency ?? null);
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const { reload: reloadBranding } = useBranding();
    const [user, setUser] = useState<CurrentUser | null>(null);
    const [loginUrl, setLoginUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [connectionFailed, setConnectionFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [offlineIdentity, setOfflineIdentity] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setConnectionFailed(false);
        auth.me(controller.signal)
            .then((payload) => {
                if (controller.signal.aborted) return;
                followFormats(payload.data);
                followCompany(payload.data as CurrentUser);
                setUser(payload.data as CurrentUser);
                setOfflineIdentity(false);
                return followServerLanguage(payload.data.lang);
            })
            .catch(async (error: Error) => {
                if (error.name === "AbortError" || controller.signal.aborted) {
                    return;
                }
                if (!(error instanceof ApiError) || (error.status !== 401 && error.status !== 403 && error.code !== "not_signed_in")) {
                    if (!navigator.onLine || error instanceof TypeError) {
                        const owner = await activeOwner().catch(() => undefined);
                        if (!controller.signal.aborted && owner && owner.workspace === getWorkspace()) {
                            const company = { id: owner.company_id, name: owner.company_name };
                            const rtl = /^(ar|he|fa|ur)(_|$)/.test(owner.lang);
                            const local: CurrentUser = { id: owner.user_id, name: owner.name, login: "", is_admin: false,
                                lang: owner.lang, ui_lang: owner.lang.split("_")[0] || null,
                                direction: rtl ? "rtl" : "ltr", company, companies: [company] };
                            followFormats(local); setUser(local); setOfflineIdentity(true);
                            await followServerLanguage(owner.lang);
                            return;
                        }
                    }
                    setConnectionFailed(true);
                    return;
                }
                void lockOfflineWork().catch(() => {});
                setUser(null);
                setLoginUrl((error as { loginUrl?: string }).loginUrl ?? null);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [attempt]);

    useEffect(() => {
        if (!offlineIdentity) return;
        const verify = () => {
            if (!navigator.onLine) return;
            void auth.me().then(async payload => {
                const owner = await activeOwner();
                if (!owner || payload.data.id !== owner.user_id || payload.data.company?.id !== owner.company_id) {
                    await lockOfflineWork().catch(() => {});
                }
                setUser(payload.data as CurrentUser); setOfflineIdentity(false);
                followFormats(payload.data); followCompany(payload.data as CurrentUser);
                await followServerLanguage(payload.data.lang);
            }).catch(error => {
                if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
                    void lockOfflineWork().catch(() => {}); setUser(null); setOfflineIdentity(false);
                    setLoginUrl((error as { loginUrl?: string }).loginUrl ?? null);
                }
            });
        };
        window.addEventListener("online", verify);
        if (navigator.onLine) verify();
        return () => window.removeEventListener("online", verify);
    }, [offlineIdentity]);

    const value = useMemo<AuthState>(() => ({
        user,
        loading,
        loginUrl,
        connectionFailed,
        retryConnection() { setAttempt((value) => value + 1); },
        async chooseCompany(companyId: number) {
            await auth.chooseCompany(companyId);
            const payload = await auth.me();
            followFormats(payload.data);
            followCompany(payload.data as CurrentUser);
            setUser(payload.data as CurrentUser);
            await reloadBranding();
        },
        async chooseLanguage(code: string) {
            const payload = await auth.chooseLanguage(code);
            followFormats(payload.data);
            setUser((current) => (current
                ? { ...current, lang: payload.data.lang, ui_lang: payload.data.ui_lang,
                    direction: payload.data.direction, date_format: payload.data.date_format,
                    time_format: payload.data.time_format }
                : current));
            await i18n.changeLanguage(uiLanguage(payload.data.lang) ?? code);
        },
        async signOut() {
            await lockOfflineWork().catch(() => {});
            await disablePush();
            const where = auth.logoutUrl();
            window.location.assign(where);
        },
    }), [user, loading, loginUrl, connectionFailed, reloadBranding]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Optional for shared widgets also used outside the signed-in application. */
export function useOptionalAuth(): AuthState | null {
    return useContext(AuthContext);
}

export function useAuth(): AuthState {
    const value = useContext(AuthContext);
    if (!value) {
        throw new Error("useAuth must be used inside AuthProvider");
    }
    return value;
}
